/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1677801600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Mar 03 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "algolia",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Algolia",
          "depth": 0,
          "id": "connect-to-algolia"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Indices",
          "depth": 1,
          "id": "indices"
        }, {
          "value": "Insight events",
          "depth": 1,
          "id": "insight-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "You want to provide relevant search and discovery results and doing so heavily relies on data in your Algolia account to be accurate and fresh. By automatically indexing records and events from your data warehouse into Algolia, you no longer need to worry about data consistency because data is no longer flowing in from multiple sources in different formats, and you can focus on building world-class customer experiences."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Index records"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to indices in Algolia"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Insight events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to insight events in Algolia"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-algolia",
        children: "Connect to Algolia"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Algolia"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Algolia"
        }), " with an API key."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Application ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "API Key"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Follow ", _jsx(_components.a, {
          href: "https://www.algolia.com/doc/guides/security/api-keys/#creating-and-managing-api-keys",
          children: "these instructions"
        }), " to create your API key and provide at least these ", _jsx(_components.a, {
          href: "https://www.algolia.com/doc/guides/security/api-keys/#rights-and-restrictions",
          children: "permissions"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permissions"
            }), _jsx(_components.th, {
              children: "Required?"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "listIndexes"
              })
            }), _jsx(_components.td, {
              children: "YES"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "addObject"
              })
            }), _jsx(_components.td, {
              children: "YES"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "deleteObject"
              })
            }), _jsxs(_components.td, {
              children: ["If you choose to ", _jsx(_components.a, {
                href: "#delete-behavior",
                children: "delete records"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Algolia destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Algolia destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "indices",
        children: "Indices"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.a, {
          href: "https://support.algolia.com/hc/en-us/articles/4406981910289-What-is-an-index-",
          children: "Algolia index"
        }), " is the place where the data used by a search engine is stored. You can use Hightouch to update individual records in an index."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You must match rows in your model with objects in Algolia on the ", _jsxs(_components.a, {
          href: "https://www.algolia.com/doc/guides/sending-and-managing-data/send-and-update-your-data/#using-unique-object-identifiers",
          children: [_jsx(_components.strong, {
            children: "Object ID"
          }), " field"]
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, Algolia automatically generates a ", _jsx(_components.strong, {
            children: "Object ID"
          }), " for every new record synced, so there is no need to match an existing record."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Records in Algolia index don't need to follow a schema so you can sync any custom fields to them."
      }), "\n", _jsxs(_components.p, {
        children: ["Visit Algolia's docs for more information about ", _jsx(_components.a, {
          href: "https://www.algolia.com/doc/guides/sending-and-managing-data/prepare-your-data/#algolia-records",
          children: "Algolia records"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Clear"
        }), " is only available for update mode. ", _jsx(_components.strong, {
          children: "Delete"
        }), " is only available for upsert mode. All sync modes use the ", _jsx(_components.strong, {
          children: "Do nothing"
        }), " behavior by default."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer record in Algolia with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Unset all the mapped fields from Algolia"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsxs(_components.td, {
              children: ["Delete the synced record from your Algolia index. Your ", _jsxs(_components.a, {
                href: "#connect-to-algolia",
                children: ["API key needs the ", _jsx(_components.strong, {
                  children: "deleteObject"
                }), " ACL"]
              }), " to enable this"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "insight-events",
        children: "Insight events"
      }), "\n", _jsx(_components.p, {
        children: "Insights events lets you capture click, conversion, and view events to help you understand how your users interact with your digital experience."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "When syncing insight events to Algolia, you must provide the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event Name"
          }), ": Name of the event. Typically the action performed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event Type"
          }), ": Type of the event. The allowed values are ", _jsx(_components.code, {
            children: "\"click\""
          }), ", ", _jsx(_components.code, {
            children: "\"conversion\""
          }), ", or ", _jsx(_components.code, {
            children: "\"view\""
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Index"
          }), ": Name of the targeted index."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User Token"
          }), ": A user identifier. Depending if the user is logged-in or not, several strategies can be used from a ", _jsx(_components.code, {
            children: "sessionId"
          }), " to another technical identifier. You should always send pseudonymous or anonymous userTokens."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "YOu can also provide these optional fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Timestamp"
          }), ": Time of the event expressed in milliseconds since the ", _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Unix_time",
            children: "Unix epoch"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Query ID"
          }), ": Algolia ", _jsx(_components.code, {
            children: "queryID"
          }), ". This is required when an event is tied to a search."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Object IDs"
          }), ": An array of index ", _jsx(_components.code, {
            children: "objectIDs"
          }), ". Limited to 20 objects. An event can't have both ", _jsx(_components.code, {
            children: "objectIDs"
          }), " and filters at the same time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Filters"
          }), ": An array of filters. Limited to 10 filters. An event can't have both ", _jsx(_components.code, {
            children: "objectIDs"
          }), " and filters at the same time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Positions"
          }), ": Position of the click in the list of Algolia search results. This field is required if a ", _jsx(_components.code, {
            children: "queryID"
          }), " is provided. One position must be provided for each ", _jsx(_components.code, {
            children: "objectID"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["When an event is tied to an Algolia search, it must also provide a queryID. If that event is a click, their absolute positions should also be passed. Consult ", _jsx(_components.a, {
            href: "https://www.algolia.com/doc/api-reference/api-methods/send-events/#parameters",
            children: "Algolia's documentation"
          }), " for more information on event fields."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
